import Vue from "vue";
import userService from "@/services/userService";

// What the referral is worth, and whether it is worth anything at all.
//
// Its own module on purpose: the cards, the chatbot menus and `serviceUses`
// all need this answer, and putting it in `referralOffer` would make the tiny
// uses helper import a Vue component through it — and close an import cycle,
// since `referralOffer` already imports the card.
//
// Reward amounts live in RedpieConfig, one trio per service, and they all ship
// at 0: the referral program is dormant until someone fills them in. With
// everything at 0 the cards still rendered their promise, so whoever opened
// "Invita a tus amigos" read "te suma 0 usos — y a él/ella 0 usos", and the
// stranger arriving from WhatsApp got "se activan 0 usos para ti". Nothing
// about the referral is shown unless there is something real to give.
export const hasReferralRewards = (rewards) =>
  !!rewards &&
  ["referring", "referred", "each_five"].some((k) => Number(rewards[k]) > 0);

// Two vocabularies meet here: `check_referred` is keyed by the service's
// display name ("Corregir Test"), while the uses helpers are keyed by the short
// service key ("tests"). Cache under the short key so both sides can read it.
const SERVICE_KEY = {
  Eureka: "eureka",
  Planificaciones: "planification",
  "Pruebas Informales": "materials",
  "Documentos PIE": "documents",
  "Corregir Test": "tests",
};

// La traducción al revés. Importa acertar: `referral_rewards` en el backend
// hace `SERVICE_PREFIX.get(service, "planifications")`, o sea que un nombre que
// no reconoce NO falla — devuelve los montos de planificaciones. Preguntar con
// la clave corta da una respuesta creíble y equivocada.
const SERVICE_NAME = Object.fromEntries(
  Object.entries(SERVICE_KEY).map(([name, key]) => [key, name])
);

// short service key -> true (reparte) / false (no reparte) / null (todavía no
// preguntamos). Los cinco arrancan en null y no se agregan claves después: en
// Vue 2 una propiedad que no existía al observar el objeto no dispara a quien
// la leyó, y `usesLine` se lee desde computeds y desde los `text()` de los
// chatbots. Sembrarlas de entrada es lo que hace que la respuesta, cuando
// llega, repinte lo que ya está en pantalla.
const rewardsByService = Vue.observable({
  eureka: null,
  planification: null,
  materials: null,
  documents: null,
  tests: null,
});

export const rememberReferralRewards = (service, rewards) => {
  const key = SERVICE_KEY[service] || service;
  if (key in rewardsByService) rewardsByService[key] = hasReferralRewards(rewards);
};

// True only when we asked and the answer was "nothing". Unknown returns false.
export const referralRewardsKnownEmpty = (serviceKey) =>
  rewardsByService[serviceKey] === false;

// Preguntar una vez por servicio y por sesión, apenas se monta el flujo.
//
// Hasta acá el único que llamaba a `check_referred` era la vista de invitar, o
// sea que la respuesta llegaba DESPUÉS de que la persona hiciera clic en la
// opción que el guard existe para no mostrar. En la primera visita de cada
// usuario —toda la población del piloto— el servicio estaba "desconocido" y el
// muro invitaba igual. El guard era circular: sólo sabía que el referido estaba
// vacío una vez que alguien había abierto el referido.
//
// Se cachea la promesa, no el resultado: si dos componentes del mismo servicio
// se montan juntos, comparten una sola llamada.
const inFlight = {};

export const ensureReferralRewards = (service) => {
  const key = SERVICE_KEY[service] || service;
  if (!(key in rewardsByService)) return Promise.resolve();
  // Ya sabemos la respuesta: no se vuelve a preguntar en toda la sesión.
  if (rewardsByService[key] !== null) return Promise.resolve();
  if (!inFlight[key]) {
    inFlight[key] = userService
      .checkReferred(SERVICE_NAME[key])
      .then((data) => {
        // Sin `rewards` en la respuesta el servicio queda desconocido, no
        // vacío: `hasReferralRewards(undefined)` es false y marcaría "no
        // reparte" sobre una respuesta que nunca dijo eso.
        if (data && data.rewards) rememberReferralRewards(key, data.rewards);
      })
      .catch(() => {})
      .finally(() => {
        // Fail-open y reintentable: sin respuesta todo se comporta como antes,
        // y se borra la marca para que un montaje posterior vuelva a intentar.
        // Un timeout no puede dejar el dato apagado por el resto de la sesión.
        if (rewardsByService[key] === null) delete inFlight[key];
      });
  }
  return inFlight[key];
};
